import React, { useState, useEffect } from 'react';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import { 
  Phone as PhoneIcon,
  Email as EmailIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  ContactMail as ContactMailIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';

// Styled components
const PageContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '80vh',
  padding: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  maxWidth: '500px',
  width: '100%',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      background: alpha(theme.palette.background.paper, 0.9),
      transform: 'translateY(-2px)',
    },
    '&.Mui-focused': {
      background: alpha(theme.palette.background.paper, 1),
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  fontWeight: 600,
  fontSize: '1.1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  },
  '&:disabled': {
    background: theme.palette.grey[400],
    transform: 'none',
    boxShadow: 'none',
  },
}));

// Add new styled Tab component
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  minHeight: 48,
  flex: 1,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

// Add TabPanel component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    {...other}
  >
    {value === index && (
      <Box sx={{ pt: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const SettingAdd = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  
  // Contact Info States
  const [contactInfo1, setContactInfo1] = useState('');
  const [contactInfo2, setContactInfo2] = useState('');
  const [matchStatus, setMatchStatus] = useState(null);

  // Password States
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchStatus, setPasswordMatchStatus] = useState(null);

  // Common States
  const [loggedInUid, setLoggedInUid] = useState('');
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const phoneRegex = /^[0-9]{10}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    const uid = localStorage.getItem('uid');
    setLoggedInUid(uid);
  }, []);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setErrors({});
    setSuccessMessage('');
  };

  // Password validation and handlers
  const validatePassword = () => {
    const errors = {};
    
    if (!newPassword) {
      errors.newPassword = 'Please enter a new password';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.match = 'Passwords do not match';
    }

    return errors;
  };

  const handlePasswordChange = (e, isConfirm = false) => {
    const value = e.target.value;
    if (isConfirm) {
      setConfirmPassword(value);
      validatePasswordMatch(newPassword, value);
    } else {
      setNewPassword(value);
      validatePasswordMatch(value, confirmPassword);
    }
  };

  const validatePasswordMatch = (pass1, pass2) => {
    if (pass1 && pass2) {
      if (pass1 === pass2) {
        setPasswordMatchStatus('match');
        setErrors(prev => ({ ...prev, match: '' }));
      } else {
        setPasswordMatchStatus('mismatch');
        setErrors(prev => ({ ...prev, match: 'Passwords do not match' }));
      }
    } else {
      setPasswordMatchStatus(null);
      setErrors(prev => ({ ...prev, match: '' }));
    }
  };

  const handleUpdatePassword = async () => {
    const validationErrors = validatePassword();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/update-password`, {
        uid: loggedInUid,
        newPassword: newPassword
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setSuccessMessage('Password updated successfully!');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setErrors({ general: response.data.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleContactInfo1Change = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setContactInfo1(value);
    } else if (!/^\d*$/.test(value)) {
      setContactInfo1(value);
    }
    validateMatch(value, contactInfo2);
  };

  const handleContactInfo2Change = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setContactInfo2(value);
    } else if (!/^\d*$/.test(value)) {
      setContactInfo2(value);
    }
    console.log('value',value);
    validateMatch(contactInfo1, value);
  };

  const validateMatch = (value1, value2) => {
    if (value1 && value2) {
      if (value1 === value2) {
        setMatchStatus('match');
        setErrors(prev => ({ ...prev, match: '' }));
      } else {
        setMatchStatus('mismatch');
        setErrors(prev => ({ ...prev, match: 'Contact information does not match' }));
      }
    } else {
      setMatchStatus(null);
      setErrors(prev => ({ ...prev, match: '' }));
    }
  };

  const validateContactInfo = () => {
    const errors = {};
    
    if (!contactInfo1) {
      errors.contactInfo1 = 'Please enter your mobile number or email';
    } else if (!phoneRegex.test(contactInfo1) && !emailRegex.test(contactInfo1)) {
      errors.contactInfo1 = 'Please enter a valid mobile number or email';
    }

    if (!contactInfo2) {
      errors.contactInfo2 = 'Please confirm your mobile number or email';
    } else if (!phoneRegex.test(contactInfo2) && !emailRegex.test(contactInfo2)) {
      errors.contactInfo2 = 'Please enter a valid mobile number or email';
    }
console.log('contactInfo1',contactInfo1);
console.log('contactInfo2',contactInfo2);
    if (contactInfo1 !== contactInfo2) {
      errors.match = 'Contact information does not match';
    }

    return errors;
  };

  const handleUpdateContact = async () => {
    const validationErrors = validateContactInfo();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/update-contact`, {
        uid: loggedInUid,
        contactInfo: contactInfo1
      }, { withCredentials: true });

      if (response.data.success) {
        setSuccessMessage('Contact information updated successfully!');
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setErrors({ general: response.data.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update contact information. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <StyledPaper elevation={3}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Account Settings
          </Typography>
        </Box>

        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            },
          }}
        >
          <StyledTab 
            icon={<LockIcon />} 
            iconPosition="start" 
            label="Update Password" 
          />
          <StyledTab 
            icon={<ContactMailIcon />} 
            iconPosition="start" 
            label="Update Contact" 
          />
        </Tabs>

        {errors.general && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 3,
              borderRadius: theme.spacing(2),
              background: alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            }}
          >
            {errors.general}
          </Alert>
        )}

        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ 
              mt: 3,
              borderRadius: theme.spacing(2),
              background: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Password Update Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ position: 'relative' }}>
            <StyledTextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => handlePasswordChange(e, false)}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mt: 2, position: 'relative' }}>
            <StyledTextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handlePasswordChange(e, true)}
              error={!!errors.confirmPassword || !!errors.match}
              helperText={errors.confirmPassword || errors.match}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    {passwordMatchStatus && (
                      <Box component="span" sx={{ ml: 1 }}>
                        {passwordMatchStatus === 'match' ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </Box>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <StyledButton 
            variant="contained" 
            onClick={handleUpdatePassword}
            disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UpdateIcon />}
          >
            {loading ? 'Updating Password...' : 'Update Password'}
          </StyledButton>
        </TabPanel>

        {/* Contact Update Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ position: 'relative' }}>
            <StyledTextField
              label="Enter Mobile Number or Email"
              value={contactInfo1}
              onChange={handleContactInfo1Change}
              error={!!errors.contactInfo1}
              helperText={errors.contactInfo1}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {phoneRegex.test(contactInfo1) ? <PhoneIcon /> : <EmailIcon />}
                  </InputAdornment>
                ),
                endAdornment: matchStatus && (
                  <InputAdornment position="end">
                    {matchStatus === 'match' ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mt: 2, position: 'relative' }}>
            <StyledTextField
              label="Confirm Mobile Number or Email"
              value={contactInfo2}
              onChange={handleContactInfo2Change}
              error={!!errors.contactInfo2 || !!errors.match}
              helperText={errors.contactInfo2 || errors.match}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {phoneRegex.test(contactInfo2) ? <PhoneIcon /> : <EmailIcon />}
                  </InputAdornment>
                ),
                endAdornment: matchStatus && (
                  <InputAdornment position="end">
                    {matchStatus === 'match' ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <StyledButton 
            variant="contained" 
            onClick={handleUpdateContact}
            disabled={loading || !contactInfo1 || !contactInfo2 || contactInfo1 !== contactInfo2}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UpdateIcon />}
          >
            {loading ? 'Updating Contact...' : 'Update Contact Information'}
          </StyledButton>
        </TabPanel>
      </StyledPaper>
    </PageContainer>
  );
};

export default SettingAdd;
